import React, { Suspense } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  useParams,
  useLocation,
} from "react-router-dom";
import { Page } from "./types";
import ScrollToTop from "./ScrollToTop";
import Footer from "shared/Footer/Footer";
import PageHome from "containers/PageHome/PageHome";
import Page404 from "containers/Page404/Page404";
import SiteHeader from "containers/SiteHeader";
import CartPage from "containers/ProductDetailPage/CartPage";
import { Toaster } from "react-hot-toast";
import ProductDetailPage from "containers/ProductDetailPage/ProductDetailPage";
import FloatingCartBar from "components/FloatingCartBar";
import CartDrawer from "components/Cart/CartDrawer";
import ProductCustomizationSheet from "components/ProductCustomization/ProductCustomizationSheet";
import AuthModal from "components/Auth/AuthModal";
import { UIProvider, useUI } from "context/UIContext";
import Cookies from "js-cookie";

// Lazy-loaded pages (code-split for smaller initial bundle)
const CheckoutPage = React.lazy(() => import("containers/PageCheckout/CheckoutPage"));
const AccountPage = React.lazy(() => import("containers/AccountPage/AccountPage"));
const AccountSavelist = React.lazy(() => import("containers/AccountPage/AccountSavelist"));
const AccountOrder = React.lazy(() => import("containers/AccountPage/AccountOrder"));
const AccountPass = React.lazy(() => import("containers/AccountPage/AccountPass"));
const AccountBilling = React.lazy(() => import("containers/AccountPage/AccountBilling"));
const PageSignUp = React.lazy(() => import("containers/PageSignUp/PageSignUp"));
const PageLogin = React.lazy(() => import("containers/PageLogin/PageLogin"));
const UserDetails = React.lazy(() => import("containers/ProductDetailPage/UserDetails"));

const PageFallback = () => (
  <div className="flex items-center justify-center h-48">
    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-500 dark:border-primary-400" />
  </div>
);

const MERCHANT_STORAGE_KEY = "merchantname";

const useMerchantName = () => {
  const { merchantname } = useParams();

  React.useEffect(() => {
    if (merchantname) {
      Cookies.set(MERCHANT_STORAGE_KEY,merchantname)
    }
  }, [merchantname]);

  return merchantname;
};

const ConditionalPageHome = () => {
  const merchantname = useMerchantName();
 
  if (merchantname) {
    return <PageHome />;
  }

  return <Page404 />;
};
const ConditionalProductDetailPage = () => {
  const merchantname = useMerchantName();
 
  if (merchantname) {
    
    return <ProductDetailPage />;
  }

  return <Page404 />;
};
export const pages: Page[] = [
  //{ path: "/:merchantname/home-header-2", component: PageHome },
  //{ path: "/product-detail/:id/:merchantname", component: ProductDetailPage },
  { path: "/:merchantname/account", component: AccountPage },
  { path: "/:merchantname/account-savelists", component: AccountSavelist },
  { path: "/:merchantname/account-my-order", component: AccountOrder },
  { path: "/:merchantname/account-change-password", component: AccountPass },
  { path: "/:merchantname/account-billing", component: AccountBilling },
  { path: "/:merchantname/cart", component: CartPage },
  { path: "/:merchantname/checkout", component: CheckoutPage },
  { path: "/:merchantname/userdetails", component: UserDetails },
  // { path: "/fullmenu/:merchantname", component: FullMenu },
  { path: "/:merchantname/signup", component: PageSignUp },
  { path: "/:merchantname/login", component: PageLogin },
];
const LayoutInner = ({ children }: any) => {
  const {
    customizingProduct,
    isCustomizationOpen,
    closeCustomization,
    isAuthModalOpen,
    authModalTab,
    closeAuthModal,
  } = useUI();

  return (
    <div className="h-screen overflow-hidden flex flex-col bg-white dark:bg-slate-900">
      <header className="fixed top-0 left-0 w-full z-50 bg-white dark:bg-slate-900 shadow dark:shadow-slate-800/50">
        <SiteHeader />
      </header>

      <main className="flex-1 mt-[64px] lg:mb-[56px] mb-0 overflow-y-auto overflow-x-hidden lg:pb-0">
        <Suspense fallback={<PageFallback />}>
          {children}
        </Suspense>

        {/* Mobile: inline footer */}
        <div className="lg:hidden">
          <Footer />
        </div>
      </main>

      {/* Desktop: fixed footer */}
      <footer className="fixed bottom-0 left-0 w-full z-40 bg-white dark:bg-slate-800 shadow hidden lg:block">
        <Footer />
      </footer>

      {/* Mobile floating cart bar */}
      <FloatingCartBar />

      {/* Cart drawer overlay */}
      <CartDrawer />

      {/* Global product customization bottom sheet */}
      <ProductCustomizationSheet
        product={customizingProduct}
        isOpen={isCustomizationOpen}
        onClose={closeCustomization}
      />

      {/* Global auth modal */}
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={closeAuthModal}
        defaultTab={authModalTab}
      />
    </div>
  );
};

const Layout = ({ children }: any) => (
  <UIProvider>
    <LayoutInner>{children}</LayoutInner>
  </UIProvider>
);

const MyRoutes = () => {
  return (
    <BrowserRouter>
      <Toaster />
      <ScrollToTop />

      <Routes>
        {pages.map(({ component: Component, path }, index) => (
          <Route
            key={index}
            path={path}
            element={
              <Layout>
                <Component />
              </Layout>
            }
          />
        ))}
        <Route
          path="/:merchantname"
          element={
            <Layout>
              <ConditionalPageHome />
            </Layout>
          }
        />
        <Route
          path="/:merchantname/home-header-2"
          element={
            <Layout>
              <PageHome />
            </Layout>
          }
        />
        <Route
          path="/:merchantname/product-detail/:id"
          element={
            <Layout>
              <ConditionalProductDetailPage />
            </Layout>
          }
        />
        <Route path="*" element={<Page404 />} />
      </Routes>
    </BrowserRouter>
  );
};

export default MyRoutes;
